import { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import { LoadingSkeleton } from "@/client/components/common/loading-skeleton"
import { useToast } from "@/client/components/common/toast"
import type { Course, CourseChapter, Enrollment } from "@/lib/supabase/types"
import styles from "./course-player-page.module.css"

// SVG Icons
const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
)

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z"/>
  </svg>
)

const ShareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/>
    <polyline points="16 6 12 2 8 6"/>
    <line x1="12" y1="2" x2="12" y2="15"/>
  </svg>
)

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
  </svg>
)

const VideoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="M10 9l5 3-5 3z"/>
  </svg>
)

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
)

const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
  </svg>
)

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87"/>
    <path d="M16 3.13a4 4 0 010 7.75"/>
  </svg>
)

const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
)

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0110 0v4"/>
  </svg>
)

type TabType = "chapters" | "notes" | "related"

interface DisplayChapter {
  id: string
  title: string
  duration: string
  completed: boolean
  current: boolean
  locked: boolean
}

function formatDuration(totalMin: number): string {
  if (totalMin < 60) return `${totalMin}分钟`
  const hours = Math.floor(totalMin / 60)
  const mins = totalMin % 60
  return mins > 0 ? `${hours}小时${mins}分` : `${hours}小时`
}

function formatRating(r: number): string {
  return r.toFixed(1)
}

function formatEnrolled(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万人`
  return `${n}人`
}

function CoursePlayerLoading() {
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.backBtn}><BackIcon /></div>
        <span className={styles.headerTitle}>课程学习</span>
      </div>
      <div className={styles.videoHero}>
        <div className={styles.videoHeroBg} />
        <div className={styles.videoPlaceholder}>
          <LoadingSkeleton variant="circular" width={72} height={72} />
        </div>
      </div>
      <div className={styles.courseInfo}>
        <LoadingSkeleton variant="text" width="60%" height={14} />
        <LoadingSkeleton variant="text" width="80%" height={24} style={{ marginTop: 12 }} />
        <LoadingSkeleton variant="text" width="90%" height={14} style={{ marginTop: 8 }} />
      </div>
    </div>
  )
}

export default function CoursePlayerPage() {
  const { id, chid } = useParams<{ id: string; chid: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const toast = useToast()

  const [course, setCourse] = useState<Course | null>(null)
  const [chapters, setChapters] = useState<CourseChapter[]>([])
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>("chapters")
  const [isFavorite, setIsFavorite] = useState(false)

  const fetchData = useCallback(async () => {
    if (!id) return
    const supabase = getBrowserSupabaseClient()
    if (!supabase) { setError("数据库未配置"); setLoading(false); return }

    setLoading(true)
    setError(null)

    try {
      const { data: courseData, error: courseErr } = await supabase
        .from("courses").select("*").eq("id", id).single()

      if (courseErr || !courseData) { setError("课程不存在"); setLoading(false); return }
      setCourse(courseData)

      const [chaptersResult, enrollmentResult] = await Promise.all([
        supabase.from("course_chapters").select("*").eq("course_id", id).order("sort_order"),
        user
          ? supabase.from("enrollments").select("*").eq("course_id", id).eq("user_id", user.id).maybeSingle()
          : Promise.resolve({ data: null }),
      ])

      if (chaptersResult.data) setChapters(chaptersResult.data)
      if (enrollmentResult.data) setEnrollment(enrollmentResult.data)
    } catch {
      setError("加载失败，请重试")
      toast.error("加载课程失败")
    } finally {
      setLoading(false)
    }
  }, [id, user, toast])

  useEffect(() => { fetchData() }, [fetchData])

  async function handleChapterComplete(chapterId: string) {
    const supabase = getBrowserSupabaseClient()
    if (!supabase || !user || !enrollment) return

    const completedList = [...(enrollment.completed_chapters ?? [])]
    if (!completedList.includes(chapterId)) {
      completedList.push(chapterId)
    }

    const totalChapters = chapters.length
    const progress = Math.round((completedList.length / totalChapters) * 100)

    const { error: updateErr } = await supabase
      .from("enrollments")
      .update({
        completed_chapters: completedList,
        progress,
        status: progress >= 100 ? "completed" : "active",
        ...(progress >= 100 ? { completed_at: new Date().toISOString() } : {}),
      })
      .eq("id", enrollment.id)

    if (updateErr) {
      toast.error("更新进度失败")
      return
    }

    setEnrollment({
      ...enrollment,
      completed_chapters: completedList,
      progress,
      status: progress >= 100 ? "completed" : "active" as Enrollment["status"],
    })
    toast.success(progress >= 100 ? "恭喜完成课程!" : "已完成章节")
  }

  if (loading) return <CoursePlayerLoading />

  if (error || !course) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}><BackIcon /></button>
          <span className={styles.headerTitle}>课程学习</span>
        </div>
        <div style={{ padding: "60px 16px", textAlign: "center" }}>
          <p style={{ fontSize: 16, color: "var(--t2)", marginBottom: 16 }}>{error || "课程不存在"}</p>
          <button onClick={fetchData}
            style={{ padding: "10px 24px", borderRadius: 10, border: "1px solid var(--bd)", background: "var(--bg3)", color: "var(--t)", cursor: "pointer", fontSize: 14 }}>
            重新加载
          </button>
        </div>
      </div>
    )
  }

  const progress = enrollment?.progress ?? 0
  const completedCount = enrollment?.completed_chapters?.length ?? 0
  const totalDurationMin = chapters.reduce((sum, ch) => sum + ch.duration_min, 0)

  const displayChapters: DisplayChapter[] = chapters.map((ch) => ({
    id: ch.id,
    title: ch.title,
    duration: formatDuration(ch.duration_min),
    completed: enrollment?.completed_chapters?.includes(ch.id) ?? false,
    current: ch.id === chid,
    locked: !ch.is_free && !enrollment,
  }))

  function handleChapterClick(ch: DisplayChapter) {
    if (ch.locked) {
      toast.info("请先报名课程")
      return
    }
    navigate(`/growth/courses/${id}/chapters/${ch.id}`)
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <span className={styles.headerTitle}>{course.title}</span>
        <button className={styles.headerAction}>
          <ShareIcon />
        </button>
      </div>

      {/* Video Hero */}
      <div className={styles.videoHero}>
        <div className={styles.videoHeroBg} />
        <div className={styles.videoPlaceholder}>
          <button className={styles.playBtn}>
            <PlayIcon />
          </button>
        </div>
        <div className={styles.videoProgress}>
          <div className={styles.videoProgressBar} style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Course Info */}
      <div className={styles.courseInfo}>
        <div className={styles.courseMeta}>
          {course.category && (
            <span className={`${styles.metaTag} ${styles.primary}`}>
              <BookIcon />
              {course.category}
            </span>
          )}
          <span className={`${styles.metaTag} ${styles.success}`}>
            <StarIcon />
            {formatRating(course.rating)}分
          </span>
          <span className={styles.metaTag}>
            <UsersIcon />
            {formatEnrolled(course.enrolled_count)}学习
          </span>
        </div>
        <h1 className={styles.courseTitle}>{course.title}</h1>
        {course.description && (
          <p className={styles.courseDesc}>{course.description.slice(0, 80)}</p>
        )}
      </div>

      {/* Progress Card */}
      <div className={styles.progressCard}>
        <div className={styles.progressHeader}>
          <span className={styles.progressLabel}>学习进度</span>
          <span className={styles.progressPercent}>{progress}%</span>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>
        <div className={styles.progressStats}>
          <span>已学习 {completedCount}/{chapters.length} 章节</span>
          <span>约 {formatDuration(totalDurationMin)}</span>
        </div>
      </div>

      {/* Section Tabs */}
      <div className={styles.sectionTabs}>
        {(["chapters", "notes", "related"] as const).map((tab) => (
          <button
            key={tab}
            className={`${styles.sectionTab} ${activeTab === tab ? styles.active : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "chapters" ? "章节列表" : tab === "notes" ? "学习笔记" : "相关推荐"}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "chapters" && (
        <div className={styles.chapterList}>
          {displayChapters.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 16px", color: "var(--t3)" }}>
              暂无章节内容
            </div>
          ) : (
            displayChapters.map((ch) => (
              <div
                key={ch.id}
                className={`${styles.chapterItem} ${ch.completed ? styles.completed : ""} ${ch.current ? styles.current : ""}`}
                onClick={() => handleChapterClick(ch)}
              >
                <div className={styles.chapterThumb}>
                  {ch.locked ? <LockIcon /> : <VideoIcon />}
                </div>
                <div className={styles.chapterInfo}>
                  <h3 className={styles.chapterTitle}>{ch.title}</h3>
                  <span className={styles.chapterDuration}>
                    <ClockIcon />
                    {ch.duration}
                  </span>
                </div>
                <div
                  className={`${styles.chapterStatus} ${ch.completed ? styles.completed : ""} ${ch.current ? styles.playing : ""}`}
                >
                  {ch.completed ? <CheckIcon /> : ch.current ? <PlayIcon /> : null}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === "notes" && (
        <div className={styles.notesSection}>
          <div style={{ textAlign: "center", padding: "32px 16px", color: "var(--t3)" }}>
            暂无学习笔记
          </div>
        </div>
      )}

      {activeTab === "related" && (
        <div className={styles.relatedSection}>
          <div style={{ textAlign: "center", padding: "32px 16px", color: "var(--t3)" }}>
            暂无相关推荐
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div className={styles.actionBar}>
        <button
          className={`${styles.actionBtn} ${styles.secondary}`}
          onClick={() => setIsFavorite(!isFavorite)}
        >
          <HeartIcon />
          收藏
        </button>
        {chid && enrollment && (
          <button
            className={`${styles.actionBtn} ${styles.primary}`}
            onClick={() => handleChapterComplete(chid)}
          >
            <CheckIcon />
            完成本章
          </button>
        )}
        {!chid && displayChapters.length > 0 && (
          <button
            className={`${styles.actionBtn} ${styles.primary}`}
            onClick={() => {
              const first = displayChapters.find((c) => !c.completed && !c.locked) || displayChapters[0]
              if (first && !first.locked) handleChapterClick(first)
            }}
          >
            <PlayIcon />
            {progress > 0 ? "继续学习" : "开始学习"}
          </button>
        )}
      </div>
    </div>
  )
}
